import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api-client";

// ═══════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════

export interface InputChannelConfig {
  allowedBasePaths: string[];
}

// ═══════════════════════════════════════════════════════════════
// Hook
// ═══════════════════════════════════════════════════════════════

export function useInputChannelConfig() {
  const [config, setConfig] = useState<InputChannelConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await api.get<{ config: InputChannelConfig }>("/input-channel-config");
      setConfig(data.config);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (allowedBasePaths: string[]): Promise<InputChannelConfig> => {
    const result = await api.put<{ config: InputChannelConfig }>("/input-channel-config", { allowedBasePaths });
    await load();
    return result.config;
  };

  return { config, isLoading, error, refresh: load, save };
}
